import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Las contrasenas no coinciden'); return; }
    if (password.length < 6) { setError('Minimo 6 caracteres'); return; }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al restablecer');
    } finally { setIsLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="bg-slate-800 border-slate-700 max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-white text-lg mb-4">Enlace invalido</p>
            <Button onClick={() => navigate('/login')} className="bg-blue-600 hover:bg-blue-700">Volver al Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b20_1px,transparent_1px),linear-gradient(to_bottom,#1e293b20_1px,transparent_1px)] bg-[size:32px_32px]" />
      <div className="relative z-10 w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src={LOGO_URL} alt="ParkingVision AI" className="h-14 w-auto" />
          <div>
            <h1 className="text-2xl font-black text-white tracking-tight">ParkingVision AI</h1>
            <p className="text-sm text-slate-400">Restablecer Contrasena</p>
          </div>
        </div>

        <Card className="bg-slate-800 border-slate-700 shadow-2xl">
          <CardHeader><CardTitle className="text-xl font-bold text-white">Nueva Contrasena</CardTitle><CardDescription className="text-slate-400">Ingresa tu nueva contrasena</CardDescription></CardHeader>
          <CardContent>
            {success ? (
              <div className="text-center py-4">
                <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <p className="text-white text-lg mb-2">Contrasena actualizada</p>
                <p className="text-slate-400 text-sm mb-4">Ya podes iniciar sesion con tu nueva contrasena</p>
                <Button onClick={() => navigate('/login')} className="bg-blue-600 hover:bg-blue-700" data-testid="go-login-btn">Ir al Login</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded text-red-400 text-sm"><AlertCircle className="w-4 h-4" /><span>{error}</span></div>}
                <div className="space-y-2">
                  <Label className="text-slate-300">Nueva contrasena</Label>
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Min 6 caracteres"
                    className="bg-slate-700 border-slate-600 text-white" data-testid="new-password-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Confirmar contrasena</Label>
                  <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="Repetir contrasena"
                    className="bg-slate-700 border-slate-600 text-white" data-testid="confirm-password-input" />
                </div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 h-11 font-semibold" disabled={isLoading} data-testid="reset-submit-btn">
                  {isLoading ? 'Guardando...' : 'Guardar Contrasena'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
